import { api } from './http.js'

/**
 * Typing addresses quickly (D-197): a Polish postal code written as 00-000 and suggesting its
 * town and voivodeship, the voivodeships to pick from, and an address pasted whole split into
 * its parts.
 */
export const VOIVODESHIPS = ['dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie', 'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie',
  'podkarpackie', 'podlaskie', 'pomorskie', 'śląskie', 'świętokrzyskie', 'warmińsko-mazurskie', 'wielkopolskie', 'zachodniopomorskie']

/** A Polish address (no country, or PL): the code is 00-000 and the regions are voivodeships. */
export const polish = (address) => !address?.country || String(address.country).toUpperCase() === 'PL'

/** Five digits typed in a row become 00-000; anything else is left as typed. */
export function formatPostalCode(value) {
  const digits = String(value || '').trim()
  return /^\d{5}$/.test(digits) ? `${digits.slice(0, 2)}-${digits.slice(2)}` : String(value || '')
}

export const completePostalCode = (value) => /^\d{2}-\d{3}$/.test(String(value || '').trim())

const known = new Map()

/** The town and region of a postal code, as Core knows them (null: unknown, or offline). */
export async function lookupPostalCode(code, country = 'PL') {
  const key = `${country}:${code}`
  if (!known.has(key)) {
    known.set(key, api('/address/postal-code', { query: { code, country }, timeoutMs: 8000 }).then((r) => r.data || null).catch(() => {
      known.delete(key)
      return null
    }))
  }
  return known.get(key)
}

const BUILDING = /^\d+[a-zA-Z]?(?:\s*[/-]\s*\d+[a-zA-Z]?)?$/

/**
 * An address written whole ("ul. Fabryczna 1/3, 10-100 Olsztyn", on one line or two) split into
 * street, building, postal code and town. Null when it holds no postal code: then it is a street.
 */
export function parseAddress(text) {
  const line = String(text || '').replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim()
  const code = line.match(/(^|[\s,])(\d{2}-\d{3})(?=[\s,]|$)/)
  if (!code) return null
  const before = line.slice(0, code.index + code[1].length).replace(/[\s,]+$/, '')
  const after = line.slice(code.index + code[0].length).replace(/^[\s,]+/, '')
  const city = after.split(',')[0].trim() || null
  const country = /,\s*(polska|poland)\s*$/i.test(after) ? 'PL' : null
  // The street: the last part before the code; its last word a number is the building.
  const streetPart = before.split(',').map((p) => p.trim()).filter(Boolean).pop() || ''
  const words = streetPart.split(' ')
  let building = null
  const last = words.length > 1 ? words[words.length - 1] : ''
  if (words.length > 3 && /^(lok\.?|m\.?)$/i.test(words[words.length - 2]) && BUILDING.test(words[words.length - 3])) {
    // "5 lok. 7": building 5, flat 7.
    building = `${words[words.length - 3]}/${words[words.length - 1]}`
    words.splice(-3)
  } else if (BUILDING.test(last)) {
    building = last.replace(/\s+/g, '')
    words.pop()
  }
  return { street: words.join(' ') || null, building, postal_code: code[2], city, ...(country ? { country } : {}) }
}
